import { useMutation } from '@tanstack/react-query';
import { saveAs } from 'file-saver';
import toast from 'react-hot-toast';
import exportService from '../../../services/export-service';

interface ExportFilters {
  start_date?: string;
  end_date?: string;
  chunk_size?: number;
  batch_size?: number;
  include_discussion?: boolean;
  identify_user?: boolean;
  tenant_id?: number;
}

const getFormattedDate = () => {
  const date = new Date();
  return date.toISOString().split('T')[0];
};

/**
 * Hook to export practices data
 */
export const useExportPractices = () => {
  return useMutation({
    mutationFn: async (filters?: ExportFilters) => {
      const toastId = toast.loading('Downloading practices data...');
      try {
        const { blob, filename } = await exportService.exportPractices(filters);
        saveAs(blob, filename || `practices_export_${getFormattedDate()}.csv`);
        toast.success('Download completed!', { id: toastId });
      } catch (error) {
        toast.error('Failed to download practices data', { id: toastId });
        throw error;
      }
    },
  });
};

/**
 * Hook to export user access analytics data
 */
export const useExportUserAccessAnalytics = () => {
  return useMutation({
    mutationFn: async (filters?: ExportFilters) => {
      const toastId = toast.loading('Downloading user access data...');
      try {
        const { blob, filename } =
          await exportService.exportUserAccessAnalytics(filters);
        saveAs(
          blob,
          filename || `user_access_analytics_export_${getFormattedDate()}.csv`
        );
        toast.success('Download completed!', { id: toastId });
      } catch (error) {
        toast.error('Failed to download user access data', { id: toastId });
        throw error;
      }
    },
  });
};

/**
 * Hook to generate user engagement analytics report (async via email)
 */
export const useGenerateUserEngagementReport = () => {
  return useMutation({
    mutationFn: async (filters?: ExportFilters) => {
      const response =
        await exportService.generateUserEngagementReport(filters);
      toast.success(
        response.message ||
          'User engagement report generation started. You will receive it via email.'
      );
      return response;
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.error ||
        'Failed to generate user engagement report';
      toast.error(errorMessage);
    },
  });
};

/**
 * Hook to generate agent analytics report (async via email)
 */
export const useGenerateAgentAnalyticsReport = () => {
  return useMutation({
    mutationFn: async (filters?: ExportFilters) => {
      const response =
        await exportService.generateAgentAnalyticsReport(filters);
      toast.success(
        response.message ||
          'Agent analytics report generation started. You will receive it via email.'
      );
      return response;
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.error ||
        'Failed to generate agent analytics report';
      toast.error(errorMessage);
    },
  });
};

/**
 * Hook to generate system access metrics report (async via email)
 */
export const useGenerateSystemAccessMetricsReport = () => {
  return useMutation({
    mutationFn: async (filters?: ExportFilters) => {
      const response =
        await exportService.generateSystemAccessMetricsReport(filters);
      toast.success(
        response.message ||
          'System access metrics report generation started. You will receive it via email.'
      );
      return response;
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.error ||
        'Failed to generate system access metrics report';
      toast.error(errorMessage);
    },
  });
};
